import { type Component } from 'solid-js';

import { ART, artUrl, backdropSrcSet } from './art';

/**
 * The painted backdrop and the forged wordmark, shared by the title screen and the gate.
 *
 * ── Why this is a component and not two copies ──────────────────────────────
 *
 * The backdrop is not one file: it is a `srcset` pair with a `sizes` hint, an overscan, a
 * filter and a drift animation, and the wordmark is a second image with a breathing
 * drop-shadow. Copying that into the login screen would be copying six decisions that have
 * to stay in step with `art.ts` — and the failure mode is a 2560px image shipped to a phone,
 * which nobody notices because it looks *right*.
 *
 * The sign-in screen is the first thing a player sees, and it was a dark panel on a black
 * page while the title screen behind it was fully painted. Sharing the art makes them one
 * place rather than two that resemble each other.
 */
export const TitleBackdrop: Component = () => (
  // Decoration with no information in it, so it is hidden from assistive technology
  // outright rather than given an alt text that describes a mood.
  <img
    class="title__art"
    src={artUrl(ART.titleBackdrop)}
    srcset={backdropSrcSet()}
    // The art is `object-fit: cover` across the whole viewport, so the candidate the
    // browser wants is the one nearest the viewport width.
    sizes="100vw"
    alt=""
    aria-hidden="true"
    data-testid="title-art"
  />
);

/**
 * The wordmark.
 *
 * **An image with real alt text**, not a background: if the file is missing, the page still
 * says what the game is called, and a screen reader reads a name rather than nothing.
 */
export const TitleMark: Component<{ class?: string }> = (props) => (
  <img
    class={props.class ?? 'title__logo'}
    src={artUrl(ART.logo)}
    alt="G.R.I.N.D."
    data-testid="title-logo"
  />
);
